/**
 * TabKeepAlive — Zero-reload multi-tab renderer
 *
 * Renders ALL open tabs simultaneously in the DOM.
 * Only the active tab is visible (flex); others are hidden (display:none).
 * This preserves each tab's React state, scroll position, and fetched data
 * across tab switches — no remount, no re-fetch.
 *
 * How params work:
 *   React Router's <Outlet> is NOT used here. Instead, we extract path params
 *   via matchPath() and inject them through PageParamsContext so that
 *   page components calling usePageParams() get the correct values.
 *
 * Navigation still works normally:
 *   - useNavigate / Link → modify main BrowserRouter URL (correct behaviour)
 *   - AppLayout.useEffect → syncs URL changes to MDI tab store
 *   - Tab visibility → driven by mdi-store activeTabId, NOT the URL
 */

import { Navigate, matchPath, useLocation } from 'react-router-dom';
import React from 'react';
import { PageParamsContext, RenderedTabContext } from '@/hooks/usePageParams';
import { shouldUseDynamicRuntimePage } from '@/lib/runtime-api';
import type { MdiTab } from '@/stores/mdi-store';

// ─── Lazy-import all page components ─────────────────────────────────────────
// (same set as App.tsx routes — keep in sync)

const DashboardPage = React.lazy(() => import('@/pages/DashboardPage'));
const DailySchedulePage = React.lazy(() => import('@/pages/oms/DailySchedulePage'));
const AiWorkforcePage = React.lazy(() => import('@/pages/aiw/AiWorkforcePage'));
const EntityListPage = React.lazy(() => import('@/pages/EntityListPage'));
const EntityDetailPage = React.lazy(() => import('@/pages/EntityDetailPage'));
const AiCommandCenter = React.lazy(() => import('@/pages/admin/AiCommandCenter'));
const SkillManagerPage = React.lazy(() => import('@/pages/admin/SkillManagerPage'));
const TenantPolicySetupPage = React.lazy(() => import('@/pages/admin/TenantPolicySetupPage'));
const LogExplorerPage = React.lazy(() => import('@/pages/admin/LogExplorerPage'));
const AlertCenterPage = React.lazy(() => import('@/pages/admin/AlertCenterPage'));
const BridgeRegistryPage = React.lazy(() => import('@/pages/admin/BridgeRegistryPage'));
const LegacyDataTransferPage = React.lazy(() => import('@/pages/admin/LegacyDataTransferPage'));
const TaskCenterPage = React.lazy(() => import('@/pages/admin/TaskCenterPage'));
const ApiCenterPage = React.lazy(() => import('@/pages/admin/ApiCenterPage'));
const AiTeamPage = React.lazy(() => import('@/pages/admin/AiTeamPage'));
const SchemaEditorPage = React.lazy(() => import('@/pages/admin/SchemaEditorPage'));
const WorkflowEditorPage = React.lazy(() => import('@/pages/admin/WorkflowEditorPage'));
const ScriptEditorPage = React.lazy(() => import('@/pages/admin/ScriptEditorPage'));
const BrainMonitorPage = React.lazy(() => import('@/pages/admin/BrainMonitorPage'));
const BrainLogsPage = React.lazy(() => import('@/pages/admin/BrainLogsPage'));
const BrainSoulPage = React.lazy(() => import('@/pages/admin/BrainSoulPage'));
const AgentLabPage = React.lazy(() => import('@/pages/admin/AgentLabPage'));
const RecurringTaskTemplatesPage = React.lazy(() => import('@/pages/admin/RecurringTaskTemplatesPage'));
const DomainConfigPage = React.lazy(() => import('@/pages/admin/DomainConfigPage'));
const MenuConfigPage = React.lazy(() => import('@/pages/admin/MenuConfigPage'));
const UserRolePage = React.lazy(() => import('@/pages/admin/UserRolePage'));
const PlatformAdminPage = React.lazy(() => import('@/pages/admin/PlatformAdminPage'));
const TenantManagementPage = React.lazy(() => import('@/pages/admin/TenantManagementPage'));
const SurfaceAccessAdminPage = React.lazy(() => import('@/pages/admin/SurfaceAccessAdminPage'));
const EInvoicingOpsPage = React.lazy(() => import('@/pages/admin/EInvoicingOpsPage'));
const PrintCenterPage = React.lazy(() => import('@/pages/admin/PrintCenterPage'));
const ConfigCompilerPage = React.lazy(() => import('@/pages/admin/ConfigCompilerPage'));
const CapabilityConfigPage = React.lazy(() => import('@/pages/admin/CapabilityConfigPage'));
const AiControlPlanePage = React.lazy(() => import('@/pages/admin/AiControlPlanePage'));
const DslDesignerPage = React.lazy(() => import('@/pages/admin/DslDesignerPage'));
const BuilderStudioPage = React.lazy(() => import('@/pages/admin/BuilderStudioPage'));
const PackManagerPage = React.lazy(() => import('@/pages/admin/PackManagerPage'));
const CustomAppViewPage = React.lazy(() => import('@/pages/admin/CustomAppViewPage'));
const DeployControlPage = React.lazy(() => import('@/pages/admin/DeployControlPage'));
const AccessLogPage = React.lazy(() => import('@/pages/admin/AccessLogPage'));
const UserPerformancePage = React.lazy(() => import('@/pages/admin/UserPerformancePage'));
const PromptEditorPage = React.lazy(() => import('@/pages/admin/PromptEditorPage'));
const SkillConfigPage = React.lazy(() => import('@/pages/admin/SkillConfigPage'));
const TrainingDataPage = React.lazy(() => import('@/pages/admin/TrainingDataPage'));
const MemoryManagerPage = React.lazy(() => import('@/pages/admin/MemoryManagerPage'));
const DocumentTemplatePage = React.lazy(() => import('@/pages/admin/DocumentTemplatePage'));
const KnowledgeBasePage = React.lazy(() => import('@/pages/settings/KnowledgeBasePage'));
const ToolRegistryPage = React.lazy(() => import('@/pages/admin/ToolRegistryPage'));
const ConfigVersionPage = React.lazy(() => import('@/pages/admin/ConfigVersionPage'));
const PlatformDynamicHomePage = React.lazy(() => import('@/pages/admin/PlatformDynamicHomePage'));
const PlatformSystemsPage = React.lazy(() => import('@/pages/admin/PlatformSystemsPage'));
const TeamspacePage = React.lazy(() => import('@/pages/admin/TeamspacePage'));
const TeamspaceWorkspacePage = React.lazy(() => import('@/pages/admin/TeamspaceWorkspacePage'));
const CeoDeskPage = React.lazy(() => import('@/pages/admin/CeoDeskPage'));
const ProgressiveOnboardingPage = React.lazy(() => import('@/pages/admin/ProgressiveOnboardingPage'));
const PartnerApplicationsPage = React.lazy(() => import('@/pages/admin/PartnerApplicationsPage'));
const OnboardingRecordsPage = React.lazy(() => import('@/pages/admin/OnboardingRecordsPage'));
const OnboardingAssignmentPoliciesPage = React.lazy(() => import('@/pages/admin/OnboardingAssignmentPoliciesPage'));
const PlatformAnalyticsPage = React.lazy(() => import('@/pages/admin/PlatformAnalyticsPage'));
const PagePerformancePage = React.lazy(() => import('@/pages/admin/PagePerformancePage'));
const TenantCapabilityMatrixPage = React.lazy(() => import('@/pages/admin/TenantCapabilityMatrixPage'));
const ExceptionPolicyPage = React.lazy(() => import('@/pages/admin/ExceptionPolicyPage'));
const WorkflowPolicyPage = React.lazy(() => import('@/pages/admin/WorkflowPolicyPage'));
const CrmIntelligenceRulesPage = React.lazy(() => import('@/pages/admin/CrmIntelligenceRulesPage'));
const RolloutDashboardPage = React.lazy(() => import('@/pages/admin/RolloutDashboardPage'));
const SdkDataAdminPage = React.lazy(() => import('@/pages/admin/SdkDataAdminPage'));
const EmailIntakeTemplatePage = React.lazy(() => import('@/pages/admin/EmailIntakeTemplatePage'));
const ViewTemplatePage = React.lazy(() => import('@/pages/admin/ViewTemplatePage'));
const WorkflowTemplateEditorPage = React.lazy(() => import('@/pages/admin/WorkflowTemplateEditorPage'));
const BrainSchedulePage = React.lazy(() => import('@/pages/admin/BrainSchedulePage'));
const JobTimelinePage = React.lazy(() => import('@/pages/admin/JobTimelinePage'));
const WhatsAppConfigPage = React.lazy(() => import('@/pages/admin/WhatsAppConfigPage'));
const ChatPage = React.lazy(() => import('@/pages/ChatPage'));
const ConversationsInboxPage = React.lazy(() => import('@/pages/inbox/ConversationsInboxPage'));
const EmailMessagesInboxPage = React.lazy(() => import('@/pages/inbox/EmailMessagesInboxPage'));
const MessagingPage = React.lazy(() => import('@/pages/inbox/MessagingPage'));
const IntakeReviewPage = React.lazy(() => import('@/pages/intake/IntakeReviewPage'));
const SettingsPage = React.lazy(() => import('@/pages/SettingsPage'));
const SettingsGeneralPage = React.lazy(() => import('@/pages/settings/SettingsGeneralPage'));
const SettingsTeamPage = React.lazy(() => import('@/pages/settings/SettingsTeamPage'));
const SettingsBillingPage = React.lazy(() => import('@/pages/settings/SettingsBillingPage'));
const SettingsProfilePage = React.lazy(() => import('@/pages/settings/SettingsProfilePage'));
const SettingsCompanyPage = React.lazy(() => import('@/pages/settings/SettingsCompanyPage'));
const SettingsStorefrontPage = React.lazy(() => import('@/pages/settings/SettingsStorefrontPage'));
const SettingsSurfaceAccessPage = React.lazy(() => import('@/pages/settings/SettingsSurfaceAccessPage'));
const SettingsOperationsPage = React.lazy(() => import('@/pages/settings/SettingsOperationsPage'));
const PortSystemDashboardPage = React.lazy(() => import('@/pages/integrations/PortSystemDashboardPage'));
const FinancialDashboard = React.lazy(() => import('@/pages/dashboards/FinancialDashboard'));
const ExecutiveIntelligencePage = React.lazy(() => import('@/pages/dashboards/ExecutiveIntelligencePage'));
const ArPage = React.lazy(() => import('@/pages/finance/ArPage'));
const ApPage = React.lazy(() => import('@/pages/finance/ApPage'));
const ApOcrPage = React.lazy(() => import('@/pages/finance/ApOcrPage'));
const ArOcrPage = React.lazy(() => import('@/pages/finance/ArOcrPage'));
const PaymentOcrPage = React.lazy(() => import('@/pages/finance/PaymentOcrPage'));
const ReceiptsPage = React.lazy(() => import('@/pages/finance/ReceiptsPage'));
const PaymentsPage = React.lazy(() => import('@/pages/finance/PaymentsPage'));
const JournalPage = React.lazy(() => import('@/pages/finance/JournalPage'));
const ArCollectionPage = React.lazy(() => import('@/pages/finance/ArCollectionPage'));
const PaymentRunPage = React.lazy(() => import('@/pages/finance/PaymentRunPage'));
const ExchangeRatesPage = React.lazy(() => import('@/pages/finance/ExchangeRatesPage'));
const FixedAssetsPage = React.lazy(() => import('@/pages/finance/FixedAssetsPage'));
const LegalEntitiesPage = React.lazy(() => import('@/pages/finance/LegalEntitiesPage'));
const ConsolidationPage = React.lazy(() => import('@/pages/finance/ConsolidationPage'));
const ChartOfAccountsPage = React.lazy(() => import('@/pages/finance/ChartOfAccountsPage'));
const AccountingPeriodsPage = React.lazy(() => import('@/pages/finance/AccountingPeriodsPage'));
const TaxConfigPage = React.lazy(() => import('@/pages/finance/TaxConfigPage'));
const BankAccountsPage = React.lazy(() => import('@/pages/finance/BankAccountsPage'));
const BankStatementsPage = React.lazy(() => import('@/pages/finance/BankStatementsPage'));
const ReconciliationPage = React.lazy(() => import('@/pages/finance/ReconciliationPage'));
const NettingPage = React.lazy(() => import('@/pages/finance/NettingPage'));
const FinanceDocCenterPage = React.lazy(() => import('@/pages/finance/FinanceDocCenterPage'));
const EInvoicingLabPage = React.lazy(() => import('@/pages/finance/EInvoicingLabPage'));
const ChargeCodesPage = React.lazy(() => import('@/pages/finance/ChargeCodesPage'));
const FinancialSetupPage = React.lazy(() => import('@/pages/finance/FinancialSetupPage'));
const ArAgingPage = React.lazy(() => import('@/pages/finance/reports/ArAgingPage'));
const ApAgingPage = React.lazy(() => import('@/pages/finance/reports/ApAgingPage'));
const SoaPage = React.lazy(() => import('@/pages/finance/reports/SoaPage'));
const GlPage = React.lazy(() => import('@/pages/finance/reports/GlPage'));
const TrialBalancePage = React.lazy(() => import('@/pages/finance/reports/TrialBalancePage'));
const PlPage = React.lazy(() => import('@/pages/finance/reports/PlPage'));
const BalanceSheetPage = React.lazy(() => import('@/pages/finance/reports/BalanceSheetPage'));
const CashFlowPage = React.lazy(() => import('@/pages/finance/reports/CashFlowPage'));
const TaxReportPage = React.lazy(() => import('@/pages/finance/reports/TaxReportPage'));
const ManagementAccountsPage = React.lazy(() => import('@/pages/finance/reports/ManagementAccountsPage'));
const AuditReportPage = React.lazy(() => import('@/pages/finance/reports/AuditReportPage'));
const ProcessAuditPage = React.lazy(() => import('@/pages/finance/reports/ProcessAuditPage'));
const PlComparePage = React.lazy(() => import('@/pages/finance/reports/PlComparePage'));
const WipRevenuePage = React.lazy(() => import('@/pages/finance/reports/WipRevenuePage'));
const CategoryWorkspacePage = React.lazy(() => import('@/pages/CategoryWorkspacePage'));
const OperationalIntelligenceHomePage = React.lazy(() => import('@/pages/operational-intelligence/OperationalIntelligenceHomePage'));
const OperationalIntelligenceSectionPage = React.lazy(() => import('@/pages/operational-intelligence/OperationalIntelligenceSectionPage'));
const OperationalIntelligenceDataRecordsPage = React.lazy(() => import('@/pages/operational-intelligence/OperationalIntelligenceDataRecordsPage'));
const OperationalIntelligenceDocumentsPage = React.lazy(() => import('@/pages/operational-intelligence/OperationalIntelligenceDocumentsPage'));
const OperationalSignalCapturePage = React.lazy(() => import('@/pages/operational-intelligence/OperationalSignalCapturePage'));
// TMS
const TmsDashboard = React.lazy(() => import('@/pages/tms/TmsDashboard'));
const TmsJobsPage = React.lazy(() => import('@/pages/tms/TmsJobsPage'));
const TmsJobDetailPage = React.lazy(() => import('@/pages/tms/TmsJobDetailPage'));
const TmsOutboxPage = React.lazy(() => import('@/pages/tms/TmsOutboxPage'));
const TmsPlannerPage = React.lazy(() => import('@/pages/tms/TmsPlannerPage'));
const FclPlannerPage = React.lazy(() => import('@/pages/tms/FclPlannerPage'));
const LclPlannerPage = React.lazy(() => import('@/pages/tms/LclPlannerPage'));
const TmsDispatchPage = React.lazy(() => import('@/pages/tms/TmsDispatchPage'));
const TmsMasterDataPage = React.lazy(() => import('@/pages/tms/TmsMasterDataPage'));
const TmsCostingPage = React.lazy(() => import('@/pages/tms/TmsCostingPage'));
const TmsRatesPage = React.lazy(() => import('@/pages/tms/TmsRatesPage'));
const TmsPricingPage = React.lazy(() => import('@/pages/tms/TmsPricingPage'));
const TmsSetupPage = React.lazy(() => import('@/pages/tms/TmsSetupPage'));
const TmsTripsPage = React.lazy(() => import('@/pages/tms/TmsTripsPage'));
const TmsSettlementPage = React.lazy(() => import('@/pages/tms/TmsSettlementPage'));
const TmsPipelinePage = React.lazy(() => import('@/pages/tms/TmsPipelinePage'));
const TmsEventLogPage = React.lazy(() => import('@/pages/tms/TmsEventLogPage'));
const TmsDriverLogPage = React.lazy(() => import('@/pages/tms/TmsDriverLogPage'));
const TmsReportCustomerPage = React.lazy(() => import('@/pages/tms/TmsReportCustomerPage'));
const TmsReportDatePage = React.lazy(() => import('@/pages/tms/TmsReportDatePage'));
const TmsReportDriverPayPage = React.lazy(() => import('@/pages/tms/TmsReportDriverPayPage'));
// Keep logistics document routes mounted in tab keep-alive to avoid full app crashes
// when the route map references the shared documents workspace.
const WmsDashboard = React.lazy(() => import('@/pages/wms/WmsDashboard'));
const WmsMasterDataPage = React.lazy(() => import('@/pages/wms/WmsMasterDataPage'));
const WmsLocationSetupPage = React.lazy(() => import('@/pages/wms/WmsLocationSetupPage'));
const WmsInboundPage = React.lazy(() => import('@/pages/wms/WmsInboundPage'));
const WmsInboundEntryPage = React.lazy(() => import('@/pages/wms/WmsInboundEntryPage'));
const WmsInspectionPage = React.lazy(() => import('@/pages/wms/WmsInspectionPage'));
const WmsInventoryPage = React.lazy(() => import('@/pages/wms/WmsInventoryPage'));
const WmsOutboundPage = React.lazy(() => import('@/pages/wms/WmsOutboundPage'));
const WmsPurchaseOrdersPage = React.lazy(() => import('@/pages/wms/WmsPurchaseOrdersPage'));
const WmsPurchaseOrderDetailPage = React.lazy(() => import('@/pages/wms/WmsPurchaseOrderDetailPage'));
const WmsMaterialRequestsPage = React.lazy(() => import('@/pages/wms/WmsMaterialRequestsPage'));
const WmsMaterialRequestDetailPage = React.lazy(() => import('@/pages/wms/WmsMaterialRequestDetailPage'));
const WmsIssueOrdersPage = React.lazy(() => import('@/pages/wms/WmsIssueOrdersPage'));
const WmsIssueOrderDetailPage = React.lazy(() => import('@/pages/wms/WmsIssueOrderDetailPage'));
const WmsReturnFormWorkflowPage = React.lazy(() => import('@/pages/wms/WmsReturnFormWorkflowPage'));
const WmsReturnFormsPage = React.lazy(() => import('@/pages/wms/WmsReturnFormsPage'));
const WmsProjectReturnsPage = React.lazy(() => import('@/pages/wms/WmsProjectReturnsPage'));
const WmsMessagesPage = React.lazy(() => import('@/pages/wms/WmsMessagesPage'));
const WmsPendingApprovalsPage = React.lazy(() => import('@/pages/wms/WmsPendingApprovalsPage'));
const WmsAuditLogPage = React.lazy(() => import('@/pages/wms/WmsAuditLogPage'));
const WmsWorkOrdersPage = React.lazy(() => import('@/pages/wms/WmsWorkOrdersPage'));
const WmsStockTakePage = React.lazy(() => import('@/pages/wms/WmsStockTakePage'));
const WmsReportsPage = React.lazy(() => import('@/pages/wms/WmsReportsPage'));
const WmsDocumentsPage = React.lazy(() => import('@/pages/wms/WmsDocumentsPage'));
const WmsPrintLabelsPage = React.lazy(() => import('@/pages/wms/WmsPrintLabelsPage'));
const WmsTaskQueuePage = React.lazy(() => import('@/pages/wms/WmsTaskQueuePage'));
const WmsScanPage = React.lazy(() => import('@/pages/wms/WmsScanPage'));
const WmsStockLedgerPage = React.lazy(() => import('@/pages/wms/WmsStockLedgerPage'));
const WmsSetupPage = React.lazy(() => import('@/pages/wms/WmsSetupPage'));
const DynamicModuleRuntimePage = React.lazy(() => import('@/pages/dynamic-runtime/DynamicModuleRuntimePage'));
const SdkOnlineModuleRuntimePage = React.lazy(() => import('@/pages/sdk/SdkOnlineModuleRuntimePage'));
const HmsMasterDataPage = React.lazy(() => import('@/pages/hms/HmsMasterDataPage'));
const HmsPricingPage = React.lazy(() => import('@/pages/hms/HmsPricingPage'));
const HmsSetupPage = React.lazy(() => import('@/pages/hms/HmsSetupPage'));
const FlmWorkspacePage = React.lazy(() => import('@/pages/flm/FlmWorkspacePage'));
// Driver
const DriverTripsPage = React.lazy(() => import('@/pages/driver/DriverTripsPage'));
const DriverTripDetailPage = React.lazy(() => import('@/pages/driver/DriverTripDetailPage'));
const DriverDeliveryPage = React.lazy(() => import('@/pages/driver/DriverDeliveryPage'));
// Scanner
const ScannerPage = React.lazy(() => import('@/pages/ScannerPage'));
// FMS
const CmsPro2ListPage = React.lazy(() => import('@/pages/pro2/CmsPro2ListPage'));
// Liner / Depot / SOC
const LinerDashboard = React.lazy(() => import('@/pages/liner/LinerDashboard'));
const LinerAccountsPage = React.lazy(() => import('@/pages/liner/LinerAccountsPage'));
const LinerBillingPage = React.lazy(() => import('@/pages/liner/LinerBillingPage'));
const LinerBookingsPage = React.lazy(() => import('@/pages/liner/LinerBookingsPage'));
const LinerCommercialPage = React.lazy(() => import('@/pages/liner/LinerCommercialPage'));
const LinerDocumentationPage = React.lazy(() => import('@/pages/liner/LinerDocumentationPage'));
const LinerEmailIntakePage = React.lazy(() => import('@/pages/liner/LinerEmailIntakePage'));
const LinerReleasePage = React.lazy(() => import('@/pages/liner/LinerReleasePage'));
const LinerLocalServicesPage = React.lazy(() => import('@/pages/liner/LinerLocalServicesPage'));
const LinerMasterDataPage = React.lazy(() => import('@/pages/liner/LinerMasterDataPage'));
const LinerPortCallsPage = React.lazy(() => import('@/pages/liner/LinerPortCallsPage'));
const LinerSchedulesPage = React.lazy(() => import('@/pages/liner/LinerSchedulesPage'));
const LinerSettingsPage = React.lazy(() => import('@/pages/liner/LinerSettingsPage'));
const LinerPrincipalTasksPage = React.lazy(() => import('@/pages/liner/LinerPrincipalTasksPage'));
const LinerTallyControlPage = React.lazy(() => import('@/pages/liner/LinerTallyControlPage'));
const DepotDashboard = React.lazy(() => import('@/pages/depot/DepotDashboard'));
const DepotAccountsPage = React.lazy(() => import('@/pages/depot/DepotAccountsPage'));
const DepotProfitabilityPage = React.lazy(() => import('@/pages/depot/DepotProfitabilityPage'));
const DepotRevenuePage = React.lazy(() => import('@/pages/depot/DepotRevenuePage'));
const DepotTariffPage = React.lazy(() => import('@/pages/depot/DepotTariffPage'));
const DepotGatePage = React.lazy(() => import('@/pages/depot/DepotGatePage'));
const DepotYardPage = React.lazy(() => import('@/pages/depot/DepotYardPage'));
const DepotContainersPage = React.lazy(() => import('@/pages/depot/DepotContainersPage'));
const DepotInspectionsPage = React.lazy(() => import('@/pages/depot/DepotInspectionsPage'));
const DepotMovesPage = React.lazy(() => import('@/pages/depot/DepotMovesPage'));
const DepotMaintenancePage = React.lazy(() => import('@/pages/depot/DepotMaintenancePage'));
const DepotBillingPage = React.lazy(() => import('@/pages/depot/DepotBillingPage'));
const SocDashboard = React.lazy(() => import('@/pages/soc/SocDashboard'));
const SocCommercialPage = React.lazy(() => import('@/pages/soc/SocCommercialPage'));
const SocContractsPage = React.lazy(() => import('@/pages/soc/SocContractsPage'));
const SocRevenuePage = React.lazy(() => import('@/pages/soc/SocRevenuePage'));
const SocPipelinePage = React.lazy(() => import('@/pages/soc/SocPipelinePage'));
const SocEquipmentPage = React.lazy(() => import('@/pages/soc/SocEquipmentPage'));
const SocReleasePage = React.lazy(() => import('@/pages/soc/SocReleasePage'));
const SocExceptionsPage = React.lazy(() => import('@/pages/soc/SocExceptionsPage'));
const SocRepairsPage = React.lazy(() => import('@/pages/soc/SocRepairsPage'));
const SocBillingPage = React.lazy(() => import('@/pages/soc/SocBillingPage'));
// HMS
const HmsDashboard = React.lazy(() => import('@/pages/hms/HmsDashboard'));
const HmsContainersPage = React.lazy(() => import('@/pages/hms/HmsContainersPage'));
const HmsJobDetailPage = React.lazy(() => import('@/pages/hms/HmsJobDetailPage'));
const HmsTr128AuditPage = React.lazy(() => import('@/pages/hms/HmsTr128AuditPage'));
const HmsCo2ePage = React.lazy(() => import('@/pages/hms/HmsCo2ePage'));
const HmsJobsPage = React.lazy(() => import('@/pages/hms/HmsJobsPage'));
const HmsTripsPage = React.lazy(() => import('@/pages/hms/HmsTripsPage'));
const HmsCostingPage = React.lazy(() => import('@/pages/hms/HmsCostingPage'));
const HmsRatesPage = React.lazy(() => import('@/pages/hms/HmsRatesPage'));
const HmsBillingPage = React.lazy(() => import('@/pages/hms/HmsBillingPage'));
const HmsControlRoomPage = React.lazy(() => import('@/pages/hms/HmsControlRoomPage'));
const HmsMatchBoxReuseLeadsPage = React.lazy(() => import('@/pages/hms/HmsMatchBoxReuseLeadsPage'));
const HmsReportCustomerPage = React.lazy(() => import('@/pages/hms/HmsReportCustomerPage'));
const DisputesPage = React.lazy(() => import('@/pages/audit/DisputesPage'));
const BillAuditPage = React.lazy(() => import('@/pages/audit/BillAuditPage'));

const BillingWorkspacePage = React.lazy(() => import('@/pages/finance/BillingWorkspacePage'));
const TmsBillingPage = React.lazy(() => import('@/pages/tms/TmsBillingPage'));
const FmsAccountingPage = React.lazy(() => import('@/pages/fms/FmsAccountingPage'));
const FmsCostingPage = React.lazy(() => import('@/pages/fms/FmsCostingPage'));

function LazyTabPage({ Component }: { Component: React.ComponentType }) {
  return (
    <React.Suspense
      fallback={
        <div className="flex min-h-[240px] items-center justify-center">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-muted border-t-primary" />
        </div>
      }
    >
      <Component />
    </React.Suspense>
  );
}
// HRM
// SVC
const SvcDashboard = React.lazy(() => import('@/pages/svc/SvcDashboard'));
const SvcOrdersPage = React.lazy(() => import('@/pages/svc/SvcOrdersPage'));
const SvcDispatchPage = React.lazy(() => import('@/pages/svc/SvcDispatchPage'));
const SvcEquipmentPage = React.lazy(() => import('@/pages/svc/SvcEquipmentPage'));
const SvcCrewPage = React.lazy(() => import('@/pages/svc/SvcCrewPage'));
const SvcSitesPage = React.lazy(() => import('@/pages/svc/SvcSitesPage'));
const SvcBillingPage = React.lazy(() => import('@/pages/svc/SvcBillingPage'));
const SvcCostingPage = React.lazy(() => import('@/pages/svc/SvcCostingPage'));
const SvcReportsPage = React.lazy(() => import('@/pages/svc/SvcReportsPage'));
const SvcFinancePage = React.lazy(() => import('@/pages/svc/SvcFinancePage'));
// SCM
const ScmDashboard = React.lazy(() => import('@/pages/scm/ScmDashboard'));
const ScmSalesOrdersPage = React.lazy(() => import('@/pages/scm/ScmSalesOrdersPage'));
const ScmPurchaseOrdersPage = React.lazy(() => import('@/pages/scm/ScmPurchaseOrdersPage'));
const ScmProductsPage = React.lazy(() => import('@/pages/scm/ScmProductsPage'));
const ScmInventoryPage = React.lazy(() => import('@/pages/scm/ScmInventoryPage'));
const ScmQuotationsPage = React.lazy(() => import('@/pages/scm/ScmQuotationsPage'));
const ScmSuppliersPage = React.lazy(() => import('@/pages/scm/ScmSuppliersPage'));
const ScmRfqPage = React.lazy(() => import('@/pages/scm/ScmRfqPage'));
const ScmSalespersonsPage = React.lazy(() => import('@/pages/scm/ScmSalespersonsPage'));
const ScmStockMovementsPage = React.lazy(() => import('@/pages/scm/ScmStockMovementsPage'));
const ScmFulfillmentPage = React.lazy(() => import('@/pages/scm/ScmFulfillmentPage'));
const ScmShipmentTrackingPage = React.lazy(() => import('@/pages/scm/ScmShipmentTrackingPage'));
const ScmCostingPage = React.lazy(() => import('@/pages/scm/ScmCostingPage'));
const ScmReportsPage = React.lazy(() => import('@/pages/scm/ScmReportsPage'));
// Network
const NetworkCirclesPage = React.lazy(() => import('@/pages/network/NetworkCirclesPage'));
const GroupPage = React.lazy(() => import('@/pages/network/GroupPage'));
const NetworkRfqPage = React.lazy(() => import('@/pages/network/NetworkRfqPage'));
const NetworkDirectoryPage = React.lazy(() => import('@/pages/network/NetworkDirectoryPage'));
const NetworkConnectionsPage = React.lazy(() => import('@/pages/network/NetworkConnectionsPage'));
const NetworkMessagesPage = React.lazy(() => import('@/pages/network/NetworkMessagesPage'));
const NetworkChannelsPage = React.lazy(() => import('@/pages/network/NetworkChannelsPage'));
const NetworkAlliancePage = React.lazy(() => import('@/pages/network/NetworkAlliancePage'));
// Business Builder
const BusinessBuilderHomePage = React.lazy(() => import('@/pages/builder/BusinessBuilderHomePage'));
const BusinessBuilderArtifactsPage = React.lazy(() => import('@/pages/builder/BusinessBuilderArtifactsPage'));
const BusinessBuilderTreePage = React.lazy(() => import('@/pages/builder/BusinessBuilderTreePage'));
const BusinessBuilderMapPage = React.lazy(() => import('@/pages/builder/BusinessBuilderMapPage'));
const BusinessBuilderAiCosWorkbenchPage = React.lazy(() => import('@/pages/builder/BusinessBuilderAiCosWorkbenchPage'));
// OMS
const OmsPipelinePage = React.lazy(() => import('@/pages/oms/OmsPipelinePage'));
const CrmSignalsPage = React.lazy(() => import('@/pages/crm/CrmSignalsPage'));
const CrmPipelinePage = React.lazy(() => import('@/pages/crm/CrmPipelinePage'));
const CrmQuotationsPage = React.lazy(() => import('@/pages/crm/CrmQuotationsPage'));
const CrmActivitiesPage = React.lazy(() => import('@/pages/crm/CrmActivitiesPage'));
const CrmRecoveryPage = React.lazy(() => import('@/pages/crm/CrmRecoveryPage'));
const CrmErpIntegrationPage = React.lazy(() => import('@/pages/crm/CrmErpIntegrationPage'));
const CrmContactsPage = React.lazy(() => import('@/pages/crm/CrmContactsPage'));
const CrmAccountsPage = React.lazy(() => import('@/pages/crm/CrmAccountsPage'));
// Cross-module
const ReportsHubPage = React.lazy(() => import('@/pages/ReportsHubPage'));

function RuntimeModuleRouteSwitchPage() {
  const location = useLocation();
  return shouldUseDynamicRuntimePage(location.pathname)
    ? <DynamicModuleRuntimePage />
    : <SdkOnlineModuleRuntimePage />;
}

// ─── Route → Component map ────────────────────────────────────────────────────
// Order matters: more specific patterns first.

type RouteEntry = {
  pattern: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.ComponentType<any>;
  /** When true the page fills the entire tab area (no p-4 wrapper, no parent scroll). */
  fullBleed?: boolean;
  /** For full-bleed pages that are document-like and still need normal page scrolling. */
  scrollableFullBleed?: boolean;
};

const OiManagementBriefPage = () => <OperationalIntelligenceSectionPage sectionId="management-brief" />;
const OiIncidentsPage = () => <OperationalIntelligenceSectionPage sectionId="incidents" />;
const OiServiceCentreOpsPage = () => <OperationalIntelligenceSectionPage sectionId="service-centre-ops" />;
const OiInventoryAnomalyPage = () => <OperationalIntelligenceSectionPage sectionId="inventory-anomaly" />;
const OiWarrantyComplaintsPage = () => <OperationalIntelligenceSectionPage sectionId="warranty-complaints" />;
const OiEhsPage = () => <OperationalIntelligenceSectionPage sectionId="ehs" />;
const OiFleetHealthPage = () => <OperationalIntelligenceSectionPage sectionId="fleet-health" />;
const FlmDashboardPage = () => <FlmWorkspacePage mode="dashboard" />;
const FlmAssetsPage = () => <FlmWorkspacePage mode="assets" />;
const FlmWorkOrdersPage = () => <FlmWorkspacePage mode="workshop" />;
const FlmPartsPage = () => <FlmWorkspacePage mode="parts" />;
const FlmTyresPage = () => <FlmWorkspacePage mode="tyres" />;
const FlmCostingPage = () => <FlmWorkspacePage mode="costing" />;
const FlmReportsPage = () => <FlmWorkspacePage mode="reports" />;
const ScmCustomersRedirect = () => <Navigate to="/e/customers" replace />;

const ROUTES: RouteEntry[] = [
  { pattern: '/', Component: DashboardPage },
  { pattern: '/today', Component: DailySchedulePage },
  { pattern: '/aiw', Component: AiWorkforcePage },
  { pattern: '/aiw/inbox', Component: AiWorkforcePage },
  { pattern: '/workspace', Component: DashboardPage },
  { pattern: '/workspace/:category', Component: CategoryWorkspacePage },
  { pattern: '/e/:slug/:id', Component: EntityDetailPage },
  { pattern: '/e/:slug', Component: EntityListPage },
  { pattern: '/chat', Component: ChatPage, fullBleed: true },
  { pattern: '/chat/:convId', Component: ChatPage, fullBleed: true },
  { pattern: '/inbox/conversations', Component: ConversationsInboxPage },
  { pattern: '/inbox/email-messages', Component: EmailMessagesInboxPage },
  { pattern: '/inbox/messaging', Component: MessagingPage },
  { pattern: '/inbox/outbox', Component: TmsOutboxPage },
  { pattern: '/inbox/intake-review', Component: IntakeReviewPage },
  { pattern: '/settings/general', Component: SettingsGeneralPage },
  { pattern: '/settings/profile', Component: SettingsProfilePage },
  { pattern: '/settings/company', Component: SettingsCompanyPage },
  { pattern: '/settings/storefront', Component: SettingsStorefrontPage },
  { pattern: '/settings/surface-access', Component: SettingsSurfaceAccessPage },
  { pattern: '/settings/running-numbers', Component: SettingsOperationsPage },
  { pattern: '/settings/recycle-bin', Component: SettingsOperationsPage },
  { pattern: '/settings/activity-log', Component: SettingsOperationsPage },
  { pattern: '/settings/event-log', Component: SettingsOperationsPage },
  { pattern: '/settings/team', Component: SettingsTeamPage },
  { pattern: '/settings/billing', Component: SettingsBillingPage },
  { pattern: '/settings/templates', Component: DocumentTemplatePage },
  { pattern: '/settings/knowledge-base', Component: KnowledgeBasePage },
  { pattern: '/settings', Component: SettingsGeneralPage },
  { pattern: '/admin/command-center', Component: AiCommandCenter },
  { pattern: '/admin/skills', Component: SkillManagerPage },
  { pattern: '/admin/policies', Component: TenantPolicySetupPage },
  { pattern: '/admin/logs', Component: LogExplorerPage },
  { pattern: '/admin/access-log', Component: AccessLogPage },
  { pattern: '/admin/user-performance', Component: UserPerformancePage },
  { pattern: '/admin/alerts', Component: AlertCenterPage },
  { pattern: '/admin/bridges', Component: BridgeRegistryPage },
  { pattern: '/admin/data-transfer', Component: LegacyDataTransferPage },
  { pattern: '/admin/tasks', Component: TaskCenterPage },
  { pattern: '/admin/api-center', Component: ApiCenterPage },
  { pattern: '/admin/auth-control', Component: ApiCenterPage },
  { pattern: '/admin/team', Component: AiTeamPage },
  { pattern: '/admin/prompts', Component: PromptEditorPage },
  { pattern: '/admin/skill-config', Component: SkillConfigPage },
  { pattern: '/admin/training', Component: TrainingDataPage },
  { pattern: '/admin/memory', Component: MemoryManagerPage },
  { pattern: '/admin/templates', Component: DocumentTemplatePage },
  { pattern: '/admin/tools', Component: ToolRegistryPage },
  { pattern: '/admin/config-versions', Component: ConfigVersionPage },
  { pattern: '/admin/schema', Component: SchemaEditorPage },
  { pattern: '/admin/workflows', Component: WorkflowEditorPage },
  { pattern: '/admin/scripts', Component: ScriptEditorPage },
  { pattern: '/admin/brain-monitor', Component: BrainMonitorPage },
  { pattern: '/admin/brain-logs', Component: BrainLogsPage },
  { pattern: '/admin/brain-soul', Component: BrainSoulPage },
  { pattern: '/admin/recurring-tasks', Component: RecurringTaskTemplatesPage },
  { pattern: '/admin/agentlab', Component: AgentLabPage },
  { pattern: '/admin/domain-configs', Component: DomainConfigPage },
  { pattern: '/admin/menu-config', Component: MenuConfigPage },
  { pattern: '/admin/users', Component: UserRolePage },
  { pattern: '/admin/surface-access', Component: SurfaceAccessAdminPage },
  { pattern: '/admin/platform', Component: PlatformAdminPage },
  { pattern: '/admin/tenants', Component: TenantManagementPage },
  { pattern: '/admin/e-invoicing-ops', Component: EInvoicingOpsPage },
  { pattern: '/admin/print-center', Component: PrintCenterPage },
  { pattern: '/admin/config-compiler', Component: ConfigCompilerPage },
  { pattern: '/admin/capabilities', Component: CapabilityConfigPage },
  { pattern: '/admin/ai-control-plane', Component: AiControlPlanePage },
  { pattern: '/admin/deploy-control', Component: DeployControlPage },
  { pattern: '/admin/platform-dynamic', Component: PlatformDynamicHomePage },
  { pattern: '/admin/platform-systems', Component: PlatformSystemsPage },
  { pattern: '/admin/sdk-data', Component: SdkDataAdminPage },
  { pattern: '/admin/teamspace', Component: TeamspacePage },
  { pattern: '/admin/teamspace/workspaces/:id', Component: TeamspaceWorkspacePage },
  { pattern: '/admin/ceo-desk', Component: CeoDeskPage },
  { pattern: '/admin/onboarding-control', Component: ProgressiveOnboardingPage },
  { pattern: '/admin/partner-applications', Component: PartnerApplicationsPage },
  { pattern: '/admin/onboarding-records', Component: OnboardingRecordsPage },
  { pattern: '/admin/onboarding-assignment-policies', Component: OnboardingAssignmentPoliciesPage },
  { pattern: '/admin/analytics/:view', Component: PlatformAnalyticsPage },
  { pattern: '/admin/analytics', Component: PlatformAnalyticsPage },
  { pattern: '/admin/page-performance', Component: PagePerformancePage },
  { pattern: '/admin/capability-matrix', Component: TenantCapabilityMatrixPage },
  { pattern: '/admin/exception-policies', Component: ExceptionPolicyPage },
  { pattern: '/admin/workflow-policies', Component: WorkflowPolicyPage },
  { pattern: '/admin/crm-intelligence-rules', Component: CrmIntelligenceRulesPage },
  { pattern: '/admin/rollout-dashboard', Component: RolloutDashboardPage },
  { pattern: '/admin/email-intake-templates', Component: EmailIntakeTemplatePage },
  { pattern: '/admin/view-templates', Component: ViewTemplatePage },
  { pattern: '/admin/workflow-templates', Component: WorkflowTemplateEditorPage },
  { pattern: '/admin/brain-schedule', Component: BrainSchedulePage },
  { pattern: '/admin/job-timeline', Component: JobTimelinePage },
  { pattern: '/admin/whatsapp', Component: WhatsAppConfigPage },
  { pattern: '/admin/user-roles', Component: UserRolePage },
  { pattern: '/admin/dsl-designer', Component: DslDesignerPage, fullBleed: true },
  { pattern: '/admin/builder', Component: BuilderStudioPage },
  { pattern: '/admin/packs', Component: PackManagerPage },
  { pattern: '/app/:slug', Component: CustomAppViewPage },
  // ── TMS ──
  { pattern: '/tms', Component: TmsDashboard },
  { pattern: '/tms/jobs/:jobId', Component: TmsJobDetailPage },
  { pattern: '/tms/jobs', Component: TmsJobsPage },
  { pattern: '/tms/outbox', Component: TmsOutboxPage },
  { pattern: '/tms/planner', Component: TmsPlannerPage, fullBleed: true },
  { pattern: '/tms/fcl-planner', Component: FclPlannerPage, fullBleed: true },
  { pattern: '/tms/lcl-planner', Component: LclPlannerPage, fullBleed: true },
  { pattern: '/tms/trips', Component: TmsTripsPage },
  { pattern: '/tms/costing', Component: TmsCostingPage },
  { pattern: '/tms/dispatch', Component: TmsDispatchPage },
  { pattern: '/tms/billing', Component: TmsBillingPage },
  { pattern: '/tms/settlement', Component: TmsSettlementPage },
  { pattern: '/tms/rates', Component: TmsRatesPage },
  { pattern: '/tms/event-log', Component: TmsEventLogPage },
  { pattern: '/tms/port-system', Component: PortSystemDashboardPage },
  { pattern: '/tms/documents', Component: WmsDocumentsPage },
  { pattern: '/tms/driver-log', Component: TmsDriverLogPage },
  { pattern: '/tms/master-data', Component: TmsMasterDataPage },
  { pattern: '/tms/pricing', Component: TmsPricingPage },
  { pattern: '/tms/setup', Component: TmsSetupPage },
  // ── WP (Wasco Project Warehouse) ──
  { pattern: '/wp/master-data', Component: WmsMasterDataPage },
  { pattern: '/wp/warehouse-setup', Component: WmsLocationSetupPage },
  { pattern: '/wp/setup', Component: WmsSetupPage },
  { pattern: '/wp/purchase-orders/:id', Component: WmsPurchaseOrderDetailPage },
  { pattern: '/wp/purchase-orders', Component: WmsPurchaseOrdersPage },
  { pattern: '/wp/inbound/new', Component: WmsInboundEntryPage },
  { pattern: '/wp/inbound', Component: WmsInboundPage },
  { pattern: '/wp/returns/form', Component: WmsReturnFormWorkflowPage },
  { pattern: '/wp/returns/grn', Component: WmsReturnFormsPage },
  { pattern: '/wp/returns/project', Component: WmsProjectReturnsPage },
  { pattern: '/wp/messages', Component: WmsMessagesPage },
  { pattern: '/wp/pending-approvals', Component: WmsPendingApprovalsPage },
  { pattern: '/wp/audit-log', Component: WmsAuditLogPage },
  { pattern: '/wp/inspection', Component: WmsInspectionPage },
  { pattern: '/wp/inventory', Component: WmsInventoryPage },
  { pattern: '/wp/lot-search', Component: WmsInventoryPage },
  { pattern: '/wp/outbound', Component: WmsOutboundPage },
  { pattern: '/wp/material-requests/new', Component: WmsMaterialRequestsPage },
  { pattern: '/wp/material-requests/:id', Component: WmsMaterialRequestDetailPage },
  { pattern: '/wp/material-requests', Component: WmsMaterialRequestsPage },
  { pattern: '/wp/issue-orders/:id', Component: WmsIssueOrderDetailPage },
  { pattern: '/wp/issue-orders', Component: WmsOutboundPage },
  { pattern: '/wp/work-orders', Component: WmsWorkOrdersPage },
  { pattern: '/wp/stock-take', Component: WmsStockTakePage },
  { pattern: '/wp/reports', Component: WmsReportsPage },
  { pattern: '/wp/documents', Component: WmsDocumentsPage },
  { pattern: '/wp/labels', Component: WmsPrintLabelsPage },
  { pattern: '/wp/tasks', Component: WmsTaskQueuePage },
  { pattern: '/wp/scan', Component: WmsScanPage },
  { pattern: '/wm/returns/form', Component: WmsReturnFormWorkflowPage },
  { pattern: '/wm/returns/grn', Component: WmsReturnFormsPage },
  { pattern: '/wm/messages', Component: WmsMessagesPage },
  { pattern: '/wm/pending-approvals', Component: WmsPendingApprovalsPage },
  { pattern: '/wm/audit-log', Component: WmsAuditLogPage },
  { pattern: '/wp/stock-ledger', Component: WmsStockLedgerPage },
  { pattern: '/wp', Component: WmsDashboard },
  // ── Driver ──
  { pattern: '/driver/deliver/:tripId', Component: DriverDeliveryPage },
  { pattern: '/driver/trip/:tripId', Component: DriverTripDetailPage },
  { pattern: '/driver/trips', Component: DriverTripsPage },
  // ── FLM ──
  { pattern: '/flm', Component: FlmDashboardPage },
  { pattern: '/flm/assets', Component: FlmAssetsPage },
  { pattern: '/flm/work-orders', Component: FlmWorkOrdersPage },
  { pattern: '/flm/workshop', Component: FlmWorkOrdersPage },
  { pattern: '/flm/parts', Component: FlmPartsPage },
  { pattern: '/flm/tyres', Component: FlmTyresPage },
  { pattern: '/flm/costing', Component: FlmCostingPage },
  { pattern: '/flm/reports', Component: FlmReportsPage },
  // ── Scanner ──
  { pattern: '/scanner', Component: ScannerPage },
  // ── HMS ──
  { pattern: '/hms/control-room', Component: HmsControlRoomPage },
  { pattern: '/hms', Component: HmsDashboard },
  { pattern: '/hms/jobs/:jobId', Component: HmsJobDetailPage },
  { pattern: '/hms/jobs', Component: HmsJobsPage },
  { pattern: '/hms/containers', Component: HmsContainersPage },
  { pattern: '/hms/trips', Component: HmsTripsPage },
  { pattern: '/hms/costing', Component: HmsCostingPage },
  { pattern: '/hms/billing', Component: HmsBillingPage },
  { pattern: '/hms/settlement', Component: TmsSettlementPage },
  { pattern: '/oms/pipeline', Component: OmsPipelinePage },
  { pattern: '/oms/schedule', Component: DailySchedulePage },
  { pattern: '/rm/signals', Component: CrmSignalsPage },
  { pattern: '/rm/pipeline', Component: CrmPipelinePage },
  { pattern: '/rm/quotations', Component: CrmQuotationsPage },
  { pattern: '/rm/follow-ups', Component: CrmActivitiesPage },
  { pattern: '/rm/activities', Component: CrmActivitiesPage },
  { pattern: '/rm/recovery', Component: CrmRecoveryPage },
  { pattern: '/rm/erp-bridge', Component: CrmErpIntegrationPage },
  { pattern: '/rm/contacts', Component: CrmContactsPage },
  { pattern: '/rm/accounts', Component: CrmAccountsPage },
  { pattern: '/rm/suppliers', Component: ScmSuppliersPage },
  { pattern: '/rm/carrier-partners', Component: NetworkDirectoryPage },
  { pattern: '/rm/partners', Component: NetworkDirectoryPage },
  { pattern: '/rm/connections', Component: NetworkConnectionsPage },
  { pattern: '/rm/vendor-panels', Component: NetworkCirclesPage },
  { pattern: '/rm/supplier-rfq', Component: NetworkRfqPage },
  { pattern: '/rm/rate-cards', Component: TmsRatesPage },
  { pattern: '/hms/pipeline', Component: TmsPipelinePage },
  { pattern: '/hms/rates', Component: HmsRatesPage },
  { pattern: '/hms/event-log', Component: TmsEventLogPage },
  { pattern: '/hms/port-system', Component: PortSystemDashboardPage },
  { pattern: '/hms/documents', Component: WmsDocumentsPage },
  { pattern: '/hms/matchbox-reuse', Component: HmsMatchBoxReuseLeadsPage },
  { pattern: '/hms/planner', Component: FclPlannerPage, fullBleed: true },
  { pattern: '/hms/dispatch', Component: TmsDispatchPage },
  { pattern: '/hms/driver-log', Component: TmsDriverLogPage },
  { pattern: '/hms/reports/customer', Component: HmsReportCustomerPage },
  { pattern: '/hms/reports/date', Component: TmsReportDatePage },
  { pattern: '/hms/reports/driver-pay', Component: TmsReportDriverPayPage },
  { pattern: '/hms/master-data', Component: HmsMasterDataPage },
  { pattern: '/hms/compliance/tr128', Component: HmsTr128AuditPage },
  { pattern: '/hms/compliance/co2e', Component: HmsCo2ePage },
  { pattern: '/hms/pricing', Component: HmsPricingPage },
  { pattern: '/hms/setup', Component: HmsSetupPage },
  // ── Audit ──
  { pattern: '/audit', Component: DisputesPage },
  { pattern: '/audit/disputes', Component: DisputesPage },
  { pattern: '/audit/bill-audit', Component: BillAuditPage },
  { pattern: '/audit/settlement', Component: TmsSettlementPage },
  // ── Finance ──
  { pattern: '/finance', Component: FinancialDashboard },
  { pattern: '/ac', Component: FinancialDashboard },
  { pattern: '/ac/ar', Component: ArPage },
  { pattern: '/ac/invoices', Component: ArPage },
  { pattern: '/ac/ap', Component: ApPage },
  { pattern: '/ac/payables', Component: ApPage },
  { pattern: '/ac/receipts', Component: ReceiptsPage },
  { pattern: '/ac/payments', Component: PaymentsPage },
  { pattern: '/finance/ar', Component: ArPage },
  { pattern: '/finance/ap', Component: ApPage },
  { pattern: '/finance/ap-ocr', Component: ApOcrPage },
  { pattern: '/finance/ar-ocr', Component: ArOcrPage },
  { pattern: '/finance/payment-ocr', Component: PaymentOcrPage },
  { pattern: '/finance/billing', Component: BillingWorkspacePage },
  { pattern: '/finance/receipts', Component: ReceiptsPage },
  { pattern: '/finance/payments', Component: PaymentsPage },
  { pattern: '/finance/journal', Component: JournalPage },
  { pattern: '/finance/ar-collection', Component: ArCollectionPage },
  { pattern: '/finance/payment-run', Component: PaymentRunPage },
  { pattern: '/finance/exchange-rates', Component: ExchangeRatesPage },
  { pattern: '/finance/fixed-assets', Component: FixedAssetsPage },
  { pattern: '/finance/legal-entities', Component: LegalEntitiesPage },
  { pattern: '/finance/chart-of-accounts', Component: ChartOfAccountsPage },
  { pattern: '/finance/accounting-periods', Component: AccountingPeriodsPage },
  { pattern: '/finance/tax-config', Component: TaxConfigPage },
  { pattern: '/finance/charge-codes', Component: ChargeCodesPage },
  { pattern: '/finance/netting', Component: NettingPage },
  { pattern: '/finance/doc-center', Component: FinanceDocCenterPage },
  { pattern: '/finance/costing', Component: FmsCostingPage },
  { pattern: '/finance/accounting', Component: FmsAccountingPage },
  { pattern: '/finance/setup', Component: FinancialSetupPage },
  { pattern: '/finance/reports/ar-aging', Component: ArAgingPage },
  { pattern: '/finance/reports/ap-aging', Component: ApAgingPage },
  { pattern: '/finance/reports/soa', Component: SoaPage },
  { pattern: '/finance/reports/gl', Component: GlPage },
  { pattern: '/finance/reports/trial-balance', Component: TrialBalancePage },
  { pattern: '/finance/reports/pl', Component: PlPage },
  { pattern: '/finance/reports/balance-sheet', Component: BalanceSheetPage },
  { pattern: '/finance/reports/cash-flow', Component: CashFlowPage },
  { pattern: '/finance/reports/tax', Component: TaxReportPage },
  { pattern: '/finance/reports/management', Component: ManagementAccountsPage },
  { pattern: '/finance/reports/audit-report', Component: AuditReportPage },
  { pattern: '/finance/reports/process-audit', Component: ProcessAuditPage },
  { pattern: '/finance/reports/consolidation', Component: ConsolidationPage },
  { pattern: '/finance/reports/pl-compare', Component: PlComparePage },
  { pattern: '/finance/reports/wip-revenue', Component: WipRevenuePage },
  { pattern: '/finance/e-invoicing-lab', Component: EInvoicingLabPage },
  { pattern: '/finance/bank-accounts', Component: BankAccountsPage },
  { pattern: '/finance/bank-statements', Component: BankStatementsPage },
  { pattern: '/finance/reconciliation', Component: ReconciliationPage },
  { pattern: '/finance/disputes', Component: DisputesPage },
  { pattern: '/finance/bill-audit', Component: BillAuditPage },
  { pattern: '/executive-intelligence', Component: ExecutiveIntelligencePage },
  // ── Operational Intelligence ──
  { pattern: '/operational-intelligence', Component: OperationalIntelligenceHomePage },
  { pattern: '/operational-intelligence/quick-capture', Component: OperationalSignalCapturePage },
  { pattern: '/operational-intelligence/management-brief', Component: OiManagementBriefPage },
  { pattern: '/operational-intelligence/incidents', Component: OiIncidentsPage },
  { pattern: '/operational-intelligence/service-centre-ops', Component: OiServiceCentreOpsPage },
  { pattern: '/operational-intelligence/inventory-anomaly', Component: OiInventoryAnomalyPage },
  { pattern: '/operational-intelligence/warranty-complaints', Component: OiWarrantyComplaintsPage },
  { pattern: '/operational-intelligence/ehs', Component: OiEhsPage },
  { pattern: '/operational-intelligence/fleet-health', Component: OiFleetHealthPage },
  { pattern: '/operational-intelligence/data-records', Component: OperationalIntelligenceDataRecordsPage },
  { pattern: '/operational-intelligence/data-records/documents', Component: OperationalIntelligenceDocumentsPage },
  { pattern: '/operational-intelligence/documents', Component: OperationalIntelligenceDocumentsPage },
  // ── Maritime Desk / Shipping Agency ──
  { pattern: '/maritime-desk', Component: LinerDashboard },
  { pattern: '/maritime-desk/port-calls', Component: LinerPortCallsPage },
  { pattern: '/maritime-desk/master-data', Component: LinerMasterDataPage },
  { pattern: '/maritime-desk/accounts', Component: LinerAccountsPage },
  { pattern: '/maritime-desk/commercial', Component: LinerCommercialPage },
  { pattern: '/maritime-desk/billing', Component: LinerBillingPage },
  { pattern: '/maritime-desk/bookings', Component: LinerBookingsPage },
  { pattern: '/maritime-desk/port-call-docs', Component: LinerDocumentationPage },
  { pattern: '/maritime-desk/documentation', Component: LinerDocumentationPage },
  { pattern: '/maritime-desk/intake', Component: LinerEmailIntakePage },
  { pattern: '/maritime-desk/release', Component: LinerReleasePage },
  { pattern: '/maritime-desk/local-services', Component: LinerLocalServicesPage },
  { pattern: '/maritime-desk/tally-control', Component: LinerTallyControlPage },
  { pattern: '/maritime-desk/schedules', Component: LinerSchedulesPage },
  { pattern: '/maritime-desk/principal-tasks', Component: LinerPrincipalTasksPage },
  { pattern: '/maritime-desk/settings', Component: LinerSettingsPage },
  { pattern: '/shipping-agency', Component: LinerDashboard },
  { pattern: '/shipping-agency/port-calls', Component: LinerPortCallsPage },
  { pattern: '/shipping-agency/master-data', Component: LinerMasterDataPage },
  { pattern: '/shipping-agency/accounts', Component: LinerAccountsPage },
  { pattern: '/shipping-agency/commercial', Component: LinerCommercialPage },
  { pattern: '/shipping-agency/billing', Component: LinerBillingPage },
  { pattern: '/shipping-agency/bookings', Component: LinerBookingsPage },
  { pattern: '/shipping-agency/port-call-docs', Component: LinerDocumentationPage },
  { pattern: '/shipping-agency/documentation', Component: LinerDocumentationPage },
  { pattern: '/shipping-agency/intake', Component: LinerEmailIntakePage },
  { pattern: '/shipping-agency/release', Component: LinerReleasePage },
  { pattern: '/shipping-agency/local-services', Component: LinerLocalServicesPage },
  { pattern: '/shipping-agency/tally-control', Component: LinerTallyControlPage },
  { pattern: '/shipping-agency/schedules', Component: LinerSchedulesPage },
  { pattern: '/shipping-agency/principal-tasks', Component: LinerPrincipalTasksPage },
  { pattern: '/shipping-agency/settings', Component: LinerSettingsPage },
  { pattern: '/liner', Component: LinerDashboard },
  { pattern: '/liner/port-calls', Component: LinerPortCallsPage },
  { pattern: '/liner/master-data', Component: LinerMasterDataPage },
  { pattern: '/liner/accounts', Component: LinerAccountsPage },
  { pattern: '/liner/commercial', Component: LinerCommercialPage },
  { pattern: '/liner/billing', Component: LinerBillingPage },
  { pattern: '/liner/bookings', Component: LinerBookingsPage },
  { pattern: '/liner/port-call-docs', Component: LinerDocumentationPage },
  { pattern: '/liner/documentation', Component: LinerDocumentationPage },
  { pattern: '/liner/intake', Component: LinerEmailIntakePage },
  { pattern: '/liner/release', Component: LinerReleasePage },
  { pattern: '/liner/local-services', Component: LinerLocalServicesPage },
  { pattern: '/liner/tally-control', Component: LinerTallyControlPage },
  { pattern: '/liner/schedules', Component: LinerSchedulesPage },
  { pattern: '/liner/principal-tasks', Component: LinerPrincipalTasksPage },
  { pattern: '/liner/settings', Component: LinerSettingsPage },
  // ── Depot ──
  { pattern: '/depot', Component: DepotDashboard },
  { pattern: '/depot/accounts', Component: DepotAccountsPage },
  { pattern: '/depot/profitability', Component: DepotProfitabilityPage },
  { pattern: '/depot/revenue', Component: DepotRevenuePage },
  { pattern: '/depot/tariff', Component: DepotTariffPage },
  { pattern: '/depot/gate', Component: DepotGatePage },
  { pattern: '/depot/yard', Component: DepotYardPage },
  { pattern: '/depot/containers', Component: DepotContainersPage },
  { pattern: '/depot/inspections', Component: DepotInspectionsPage },
  { pattern: '/depot/moves', Component: DepotMovesPage },
  { pattern: '/depot/maintenance', Component: DepotMaintenancePage },
  { pattern: '/depot/billing', Component: DepotBillingPage },
  // ── SOC ──
  { pattern: '/soc', Component: SocDashboard },
  { pattern: '/soc/commercial', Component: SocCommercialPage },
  { pattern: '/soc/contracts', Component: SocContractsPage },
  { pattern: '/soc/revenue', Component: SocRevenuePage },
  { pattern: '/soc/pipeline', Component: SocPipelinePage },
  { pattern: '/soc/equipment', Component: SocEquipmentPage },
  { pattern: '/soc/releases', Component: SocReleasePage },
  { pattern: '/soc/exceptions', Component: SocExceptionsPage },
  { pattern: '/soc/repairs', Component: SocRepairsPage },
  { pattern: '/soc/billing', Component: SocBillingPage },
  // ── PRO2 ──
  { pattern: '/pro2/:direction/:resource', Component: CmsPro2ListPage },
  // ── SVC ──
  { pattern: '/svc', Component: SvcDashboard },
  { pattern: '/svc/orders', Component: SvcOrdersPage },
  { pattern: '/svc/dispatch', Component: SvcDispatchPage },
  { pattern: '/svc/equipment', Component: SvcEquipmentPage },
  { pattern: '/svc/crew', Component: SvcCrewPage },
  { pattern: '/svc/sites', Component: SvcSitesPage },
  { pattern: '/svc/billing', Component: SvcBillingPage },
  { pattern: '/svc/costing', Component: SvcCostingPage },
  { pattern: '/svc/reports', Component: SvcReportsPage },
  { pattern: '/svc/finance', Component: SvcFinancePage },
  // ── SCM ──
  { pattern: '/scm', Component: ScmDashboard },
  { pattern: '/scm/sales-orders', Component: ScmSalesOrdersPage },
  { pattern: '/scm/quotations', Component: ScmQuotationsPage },
  { pattern: '/scm/customers', Component: ScmCustomersRedirect },
  { pattern: '/scm/purchase-orders', Component: ScmPurchaseOrdersPage },
  { pattern: '/scm/suppliers', Component: ScmSuppliersPage },
  { pattern: '/scm/rfq', Component: ScmRfqPage },
  { pattern: '/scm/salespersons', Component: ScmSalespersonsPage },
  { pattern: '/scm/products', Component: ScmProductsPage },
  { pattern: '/scm/inventory', Component: ScmInventoryPage },
  { pattern: '/scm/stock-movements', Component: ScmStockMovementsPage },
  { pattern: '/scm/fulfillment', Component: ScmFulfillmentPage },
  { pattern: '/scm/shipment-tracking', Component: ScmShipmentTrackingPage },
  { pattern: '/scm/costing', Component: ScmCostingPage },
  { pattern: '/scm/reports', Component: ScmReportsPage },
  // ── Network ──
  { pattern: '/network/circles', Component: NetworkCirclesPage },
  { pattern: '/network/group', Component: GroupPage },
  { pattern: '/network/rfq', Component: NetworkRfqPage },
  { pattern: '/network/directory', Component: NetworkDirectoryPage },
  { pattern: '/network/connections', Component: NetworkConnectionsPage },
  { pattern: '/network/alliances', Component: NetworkAlliancePage },
  { pattern: '/network/messages', Component: NetworkMessagesPage },
  { pattern: '/network/messages/:memberId', Component: NetworkMessagesPage },
  { pattern: '/network/channels', Component: NetworkChannelsPage },
  { pattern: '/network/channels/:channelId', Component: NetworkChannelsPage },
  // ── Business Builder owner runtime ──
  { pattern: '/builder/business-home', Component: BusinessBuilderHomePage },
  { pattern: '/builder/organization', Component: BusinessBuilderTreePage },
  { pattern: '/builder/business-tree', Component: BusinessBuilderTreePage },
  { pattern: '/builder/artifacts', Component: BusinessBuilderArtifactsPage },
  { pattern: '/builder/world-view', Component: BusinessBuilderMapPage, fullBleed: true, scrollableFullBleed: true },
  { pattern: '/builder/ai-cos-workbench', Component: BusinessBuilderAiCosWorkbenchPage, fullBleed: true, scrollableFullBleed: true },
  { pattern: '/builder/review-queue', Component: BusinessBuilderMapPage, fullBleed: true, scrollableFullBleed: true },
  { pattern: '/builder/business-map', Component: BusinessBuilderMapPage, fullBleed: true, scrollableFullBleed: true },

  // ── Cross-module Reports Hub ──
  { pattern: '/reports', Component: ReportsHubPage },

  // ── Dynamic/runtime module mounts (fallback only; keep last) ──
  { pattern: '/pwa/:moduleId/*', Component: RuntimeModuleRouteSwitchPage },
  { pattern: '/portal/:moduleId/*', Component: RuntimeModuleRouteSwitchPage },
  { pattern: '/:moduleId/*', Component: RuntimeModuleRouteSwitchPage },
];

function resolveRoute(path: string): {
  Component: React.ComponentType;
  params: Record<string, string | undefined>;
  fullBleed?: boolean;
  scrollableFullBleed?: boolean;
} | null {
  // Strip query string for matching
  const pathname = path.split('?')[0] ?? path;
  for (const route of ROUTES) {
    const match = matchPath(route.pattern, pathname);
    if (match) {
      return {
        Component: route.Component,
        params: match.params as Record<string, string | undefined>,
        fullBleed: route.fullBleed,
        scrollableFullBleed: route.scrollableFullBleed,
      };
    }
  }
  return null;
}

// ─── Per-tab ErrorBoundary ─────────────────────────────────────────────────
// Prevents a crash in ONE tab from taking down ALL tabs.

class TabErrorBoundary extends React.Component<
  { tabPath: string; children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state: { hasError: boolean; error: Error | null } = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-12 text-center gap-3">
          <p className="text-sm font-medium text-red-600">This tab encountered an error</p>
          <p className="text-xs text-muted-foreground max-w-md">{this.state.error?.message}</p>
          <button
            className="mt-2 text-xs text-primary underline hover:no-underline"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface TabKeepAliveProps {
  tabs: MdiTab[];
  activeTabId: string | null;
}

export function TabKeepAlive({ tabs, activeTabId }: TabKeepAliveProps) {
  if (tabs.length === 0) return null;

  return (
    <>
      {tabs.map((tab) => {
        const resolved = resolveRoute(tab.path);
        const isActive = tab.id === activeTabId;

        return (
          <div
            key={`${tab.id}:${tab.path}`}
            // Use CSS visibility — component stays mounted, state preserved
            className={
              isActive
                ? 'flex flex-1 flex-col min-h-0 overflow-hidden bg-background'
                : 'hidden'
            }
            // aria-hidden for accessibility when not active
            aria-hidden={!isActive}
          >
            {resolved ? (
              <TabErrorBoundary tabPath={tab.path}>
                <RenderedTabContext.Provider value={{ tabId: tab.id, tabPath: tab.path, isActive }}>
                  <PageParamsContext.Provider value={resolved.params}>
                    {resolved.fullBleed ? (
                      resolved.scrollableFullBleed ? (
                        <div className="flex flex-1 min-h-0 flex-col overflow-y-auto overscroll-contain">
                          <LazyTabPage Component={resolved.Component} />
                        </div>
                      ) : (
                        <LazyTabPage Component={resolved.Component} />
                      )
                    ) : (
                      <div className="flex flex-1 min-h-0 flex-col overflow-y-auto p-4">
                        <LazyTabPage Component={resolved.Component} />
                      </div>
                    )}
                  </PageParamsContext.Provider>
                </RenderedTabContext.Provider>
              </TabErrorBoundary>
            ) : (
              // Fallback for unresolved paths (shouldn't happen normally)
              <div className="p-8 text-center text-muted-foreground text-sm">
                Page not found: {tab.path}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
